import React, { useState, useRef, useEffect } from "react"
import { Slider, Button } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash, faRotateLeft } from "@fortawesome/free-solid-svg-icons"

import { TransformedTableData } from "utils/convertDataToTable"

/**
 * Position coordinates for dragging
 */
interface Position {
  x: number
  y: number
}

/**
 * Table dimensions interface
 */
interface TableDimensions {
  width: number
  height: number
}

/**
 * Removed elements interface
 */
interface RemovedElements {
  columns: Set<string>
  rows: Set<string>
}

/**
 * Update data payload interface
 */
interface UpdateDataPayload {
  selectedColumns: Set<string>
  selectedRows: Set<string>
}

/**
 * Props for StatisticsTable component
 */
export interface StatisticsTableProps {
  /** Transformed table data with headers and rows */
  transformedData: TransformedTableData
  /** Callback when table data is updated */
  onUpdateData: (payload: UpdateDataPayload) => void
  /** Set of removed columns and rows */
  removedElements: RemovedElements
  /** Function to set persistent rows */
  setPersistentRows: (rows: Set<string>) => void
  /** Whether table is in static (non-interactive) mode */
  isStatic?: boolean
  /** Content scale factor for responsive scaling */
  contentScale?: number
  /** Whether table should stretch to fill container */
  stretchToFill?: boolean
}

/**
 * Statistics Table Component
 *
 * A comprehensive table component that displays transformed statistical data
 * with interactive features including:
 * - Zoom and pan functionality
 * - Row and column selection
 * - Responsive scaling
 * - Static display mode for embedded use
 * - Stretch-to-fill option for container fitting
 *
 * @param props - Component props
 * @returns JSX element for statistics table display
 */
const StatisticsTable: React.FC<StatisticsTableProps> = ({
  transformedData,
  onUpdateData,
  removedElements,
  setPersistentRows,
  isStatic = false,
  contentScale = 1,
  stretchToFill = false, // Control whether table stretches to fill container
}) => {
  const { headers, rows, hasSubHeaders } = transformedData
  const tableRef = useRef<HTMLTableElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState<number>(1)
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 })
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set())
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [autoScale, setAutoScale] = useState<number>(1)
  const [tableDimensions, setTableDimensions] = useState<TableDimensions>({
    width: 0,
    height: 0,
  })
  const hasTopLeftHeader: boolean =
    hasSubHeaders &&
    headers.length > 0 &&
    headers[0].subHeaders &&
    headers[0].subHeaders.length === 2 &&
    headers[0].subHeaders[0] === "" &&
    headers[0].subHeaders[1] === "Statistic"

  // Measure table dimensions for proper scaling
  useEffect(() => {
    if (tableRef.current) {
      const updateTableDimensions = (): void => {
        if (tableRef.current) {
          setTableDimensions({
            width: tableRef.current.scrollWidth,
            height: tableRef.current.scrollHeight,
          })
        }
      }

      // Use ResizeObserver for reliable dimension tracking
      const resizeObserver = new ResizeObserver(updateTableDimensions)
      resizeObserver.observe(tableRef.current)

      // Initial measurement
      updateTableDimensions()

      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [transformedData])

  // Calculate responsive scale for static view
  useEffect(() => {
    if (isStatic && containerRef.current && tableRef.current) {
      const calculateScale = (): void => {
        // If stretchToFill is enabled, don't apply scaling
        if (stretchToFill) {
          setAutoScale(1)
          return
        }

        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth
          const containerHeight = containerRef.current.clientHeight
          const tableWidth = tableDimensions.width
          const tableHeight = tableDimensions.height

          const widthScale = containerWidth / tableWidth
          const heightScale = containerHeight / tableHeight

          const newScale = Math.min(widthScale, heightScale, 1)
          setAutoScale(newScale)
        }
      }

      calculateScale()

      const resizeObserver = new ResizeObserver(calculateScale)
      resizeObserver.observe(containerRef.current)

      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [isStatic, transformedData, contentScale, stretchToFill, tableDimensions])

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (isStatic) return
    setIsDragging(true)
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })

    // Prevent default browser drag behavior
    e.preventDefault()
  }

  const handleMouseMove = (e: MouseEvent): void => {
    if (!isDragging || isStatic) return

    // Calculate the potential new position
    const newX = e.clientX - dragStart.x
    const newY = e.clientY - dragStart.y

    // No constraints - allow moving in any direction and distance
    setPosition({
      x: newX,
      y: newY,
    })
  }

  const handleMouseUp = (): void => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (!isStatic) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart, isStatic])

  // Calculate base styles considering contentScale, with adjustment for transposed tables
  // Check if this is likely a transposed table (more columns than typical)
  const isLikelyTransposed: boolean = rows.length > 0 && rows[0].length > 8

  // Use smaller font for transposed tables
  const baseFontSize: number = isLikelyTransposed
    ? Math.max(9, Math.min(11 * contentScale, 13))
    : Math.max(10, Math.min(14 * contentScale, 16))

  const basePadding: number = isLikelyTransposed
    ? Math.max(3, Math.min(5 * contentScale, 7))
    : Math.max(4, Math.min(8 * contentScale, 12))

  const cellStyle: React.CSSProperties = {
    border: "1px solid #e2e8f0",
    padding: `${basePadding}px`,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: `${baseFontSize}px`,
    transition: "background-color 0.2s",
  }

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    backgroundColor: "#f8fafc",
    fontWeight: "bold",
    borderBottom: "2px solid #e2e8f0",
  }

  const formatCellContent = (cell: unknown): string => {
    if (cell === null || cell === undefined || cell === "null") return ""
    return String(cell)
  }

  const handleResetClick = (): void => {
    setSelectedColumns(new Set())
    setSelectedRows(new Set())
    onUpdateData({
      selectedColumns: new Set(),
      selectedRows: new Set(),
    })
    setPersistentRows(new Set())
  }

  const handleRemoveSelectedClick = (): void => {
    onUpdateData({
      selectedColumns: new Set([...selectedColumns]),
      selectedRows: new Set(
        [...selectedRows].map((rowIndex: number) => {
          const identifier =
            rows[rowIndex][0] ||
            rows
              .slice(0, rowIndex)
              .reverse()
              .find((r) => r[0])?.[0]
          return String(identifier)
        })
      ),
    })
  }

  const handleHeaderClick = (header: string): void => {
    if (isStatic) return
    const newSelected = new Set(selectedColumns)
    if (selectedColumns.has(header)) {
      newSelected.delete(header)
    } else {
      newSelected.add(header)
    }
    setSelectedColumns(newSelected)
  }

  const handleSubHeaderClick = (subHeaderKey: string): void => {
    if (isStatic) return
    const newSelected = new Set(selectedColumns)
    if (selectedColumns.has(subHeaderKey)) {
      newSelected.delete(subHeaderKey)
    } else {
      newSelected.add(subHeaderKey)
    }
    setSelectedColumns(newSelected)
  }

  const handleRowClick = (rowIndex: number, identifier: unknown): void => {
    if (!identifier || isStatic) return

    const stringIdentifier = String(identifier)
    const isRemoved = removedElements.rows.has(stringIdentifier)
    if (isRemoved) return

    const newSelected = new Set(selectedRows)
    if (selectedRows.has(rowIndex)) {
      newSelected.delete(rowIndex)
    } else {
      newSelected.add(rowIndex)
    }
    setSelectedRows(newSelected)
  }

  // Complete return statement for StatisticsTable component
  return (
    <div
      className={`flex flex-col ${stretchToFill ? "w-full h-full" : ""}`}
      style={{ margin: 0, padding: 0 }}
    >
      {!isStatic && (
        <div className="flex items-center gap-2 px-2">
          <span className="text-sm">Zoom:</span>
          <Slider
            className="w-32 text-[#36c3ed]"
            styles={{
              track: { background: "#36c3ed" },
              handle: { borderColor: "#36c3ed" },
            }}
            min={10}
            max={400}
            value={scale * 100}
            onChange={(value: number) => setScale(value / 100)}
            tooltip={{ formatter: (value?: number) => `${value}%` }}
          />
          <Button
            icon={<FontAwesomeIcon icon={faRotateLeft} />}
            onClick={handleResetClick}
            disabled={selectedColumns.size === 0 && selectedRows.size === 0}
            className="mr-2"
          >
            Reset
          </Button>
          {!hasSubHeaders && (
            <Button
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={handleRemoveSelectedClick}
              disabled={selectedColumns.size === 0 && selectedRows.size === 0}
            >
              Remove Selected
            </Button>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className={`${isStatic ? "flex items-center justify-center" : ""} ${
          stretchToFill ? "w-full h-full" : ""
        }`}
        onMouseDown={!isStatic ? handleMouseDown : undefined}
        style={{
          overflow: "visible", // Changed to visible to allow viewing content outside boundaries
          cursor: isStatic ? "default" : isDragging ? "grabbing" : "grab",
          position: "relative",
          width: stretchToFill ? "100%" : "auto",
          height: "auto", // Change to auto height
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          // Remove extra spacing
          margin: 0,
          padding: 0,
        }}
      >
        <div
          style={{
            transform: isStatic
              ? `scale(${stretchToFill ? 1 : autoScale})`
              : `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
            transformOrigin: "0 0", // Set to top-left to prevent overflow
            display: "inline-block",
            width: stretchToFill ? "100%" : "auto",
            maxWidth: "none", // Allow content to extend beyond boundaries when dragging
            margin: 0,
            padding: 0,
            // Add z-index for proper positioning during drag
            zIndex: isDragging ? 1000 : 1,
          }}
        >
          <table
            ref={tableRef}
            style={{
              borderCollapse: "collapse",
              width: stretchToFill ? "100%" : "auto",
              backgroundColor: "white",
              tableLayout: stretchToFill ? "fixed" : "auto",
            }}
          >
            <thead>
              <tr>
                {hasSubHeaders && (
                  <th rowSpan={2} style={headerCellStyle}>
                    <div
                      style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {hasTopLeftHeader
                        ? formatCellContent(headers[0].header)
                        : ""}
                    </div>
                  </th>
                )}
                {hasSubHeaders && (
                  <th rowSpan={2} style={headerCellStyle}>
                    <div
                      style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      Statistic
                    </div>
                  </th>
                )}
                {headers
                  .slice(hasTopLeftHeader ? 1 : 0) // Changed from 2 to 1
                  .map((group, groupIndex: number) => {
                    const headerString = formatCellContent(group.header)
                    const isTotals = headerString === "Totals"
                    const isClickable = !hasSubHeaders && !isTotals && !isStatic

                    // If this is a Totals header, make it span both rows
                    if (isTotals) {
                      return (
                        <th
                          key={groupIndex}
                          rowSpan={hasSubHeaders ? 2 : 1}
                          style={{
                            ...headerCellStyle,
                            backgroundColor: "#f8fafc",
                            cursor: "default",
                          }}
                        >
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {headerString}
                          </div>
                        </th>
                      )
                    }

                    return (
                      <th
                        key={groupIndex}
                        colSpan={hasSubHeaders ? group.subHeaders.length : 1}
                        style={{
                          ...headerCellStyle,
                          backgroundColor:
                            !isTotals &&
                            isClickable &&
                            selectedColumns.has(headerString)
                              ? "#e6f7ff"
                              : "#f8fafc",
                          cursor: isClickable ? "pointer" : "default",
                        }}
                        onClick={() => {
                          if (isClickable) {
                            handleHeaderClick(headerString)
                          }
                        }}
                      >
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {formatCellContent(headerString)}
                        </div>
                      </th>
                    )
                  })}
              </tr>
              {hasSubHeaders && (
                <tr>
                  {headers
                    .slice(hasTopLeftHeader ? 1 : 0) // Changed from 2 to 1
                    .flatMap((group, groupIndex: number) => {
                      // Skip rendering subheaders only for groups that are literally "Totals"
                      // since they are rendered as full-height headers above
                      const headerString = formatCellContent(group.header)
                      if (headerString === "Totals") {
                        return [] // Return empty array to skip this group
                      }

                      return group.subHeaders.map(
                        (subHeader: string, subHeaderIndex: number) => {
                          const isTotals = subHeader === "Totals"
                          const subHeaderKey = `${String(
                            group.header
                          )}//${subHeader}`
                          return (
                            <th
                              key={`${groupIndex}-${subHeaderIndex}`}
                              style={{
                                ...headerCellStyle,
                                backgroundColor:
                                  !isTotals &&
                                  !isStatic &&
                                  selectedColumns.has(subHeaderKey)
                                    ? "#e6f7ff"
                                    : "#f8fafc",
                                cursor:
                                  !isTotals && !isStatic
                                    ? "pointer"
                                    : "default",
                              }}
                              onClick={() => {
                                if (!isTotals && !isStatic) {
                                  handleSubHeaderClick(subHeaderKey)
                                }
                              }}
                            >
                              <div
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {subHeader}
                              </div>
                            </th>
                          )
                        }
                      )
                    })}
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((row, rowIndex: number) => {
                const identifier = row[0]
                const associatedIdentifier =
                  identifier ||
                  rows
                    .slice(0, rowIndex)
                    .reverse()
                    .find((r) => r[0])?.[0]

                const stringAssociatedIdentifier = String(associatedIdentifier)
                const isRemoved = removedElements.rows.has(
                  stringAssociatedIdentifier
                )
                const isSelected = selectedRows.has(rowIndex)

                return (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex: number) => {
                      const isFirstColumn = cellIndex === 0
                      const content = formatCellContent(cell)
                      return (
                        <td
                          key={cellIndex}
                          style={{
                            ...cellStyle,
                            backgroundColor: isFirstColumn
                              ? isSelected || isRemoved
                                ? "#e6f7ff"
                                : "#f8fafc"
                              : isSelected || isRemoved
                              ? "#e6f7ff"
                              : "white",
                            fontWeight: isFirstColumn ? "bold" : "normal",
                            cursor:
                              isFirstColumn && identifier && !isStatic
                                ? "pointer"
                                : "default",
                          }}
                          title={content}
                          onClick={() => {
                            if (isFirstColumn) {
                              handleRowClick(rowIndex, identifier)
                            }
                          }}
                        >
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth:
                                rows[0].length > 8
                                  ? "140px"
                                  : stretchToFill
                                  ? "none"
                                  : "200px", // Wider cells for transposed tables
                              whiteSpace: "nowrap", // Prevent text wrapping
                              maxHeight: "1.5em", // Reduce height to prevent oversized cells
                            }}
                          >
                            {content}
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default StatisticsTable
